import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const corporateClean: TemplateRegistration = {
  meta: {
    id: 'corporate-clean',
    name: 'Corporate Clean',
    category: 'Classic',
    description: 'Professional, trustworthy enterprise-grade design with clean lines and blue accents',
    previewImage: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Plus Jakarta Sans']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 based on ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section">
        <div class="container">
          <div class="about-grid">
            <div class="about-text-col">
              <span class="section-label">About Us</span>
              <h2 class="section-title">Who We Are</h2>
              <p class="about-text">${safeAbout}</p>
              ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="btn-outline">Learn More</a>` : ''}
            </div>
            <div class="about-image-col">
              ${heroImage ? `<img src="${escapeAttr(heroImage)}" alt="${safeName}">` : `<span class="about-image-placeholder">${safeName.charAt(0)}</span>`}
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card">
            <div class="service-card-top"></div>
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-gray">
        <div class="container">
          <div class="section-header">
            <span class="section-label">Our Services</span>
            <h2 class="section-title">What We Offer</h2>
          </div>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="container">
          <div class="section-header">
            <span class="section-label">Portfolio</span>
            <h2 class="section-title">Our Gallery</h2>
          </div>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-quote">&ldquo;</div>
            <p class="testimonial-text">${tContent}</p>
            ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            <div class="testimonial-author">
              <div class="testimonial-avatar">${tName.charAt(0)}</div>
              <div>
                <strong>${tName}</strong>
                ${tRole ? `<span>${tRole}</span>` : ''}
              </div>
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-gray">
        <div class="container">
          <div class="section-header">
            <span class="section-label">Testimonials</span>
            <h2 class="section-title">What Our Clients Say</h2>
          </div>
          <div class="testimonials-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${getIconSvg('help-circle')} ${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="container">
          <div class="section-header">
            <span class="section-label">FAQ</span>
            <h2 class="section-title">Frequently Asked Questions</h2>
          </div>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-gray">
        <div class="container">
          <div class="section-header">
            <span class="section-label">Pricing</span>
            <h2 class="section-title">Our Plans</h2>
          </div>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="container">
          <div class="section-header">
            <span class="section-label">Schedule</span>
            <h2 class="section-title">Business Hours</h2>
          </div>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-gray">
        <div class="container">
          <div class="section-header">
            <span class="section-label">Contact</span>
            <h2 class="section-title">Get In Touch</h2>
          </div>
          <div class="contact-cards">
            <div class="contact-card">
              <div class="contact-card-title">Phone</div>
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<div><a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div></div>` : `<p style="color:var(--text-muted);font-size:.9rem;">Contact us via the form</p>`}
            </div>
            <div class="contact-card">
              <div class="contact-card-title">Address</div>
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<div><span>${sanitizeText(address)}</span></div></div>` : `<p style="color:var(--text-muted);font-size:.9rem;">Get in touch for our location</p>`}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps &rarr;</a>` : ''}
            </div>
            <div class="contact-card">
              <div class="contact-card-title">Send a Message</div>
              <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
                <input type="text" placeholder="Full Name" required>
                <input type="email" placeholder="Email Address" required>
                <textarea placeholder="Your Message" rows="4" required></textarea>
                <button type="submit">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>`);
    }

    // Stats bar data
    const statsItems: string[] = [];
    if (reviewCount) statsItems.push(`${reviewCount}+ Clients`);
    if (rating) statsItems.push(`${rating.toFixed(1)} Rating`);
    statsItems.push(`${data.services.length}+ Services`);

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #ffffff; --bg-gray: #f8fafc; --primary: #1e40af;
      --primary-light: #3b82f6; --text: #0f172a; --text-muted: #64748b;
      --border: #e2e8f0; --font: 'Plus Jakarta Sans', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font); background: var(--bg); color: var(--text); line-height: 1.7; }
    .container { max-width: 1140px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--primary); text-decoration: none; transition: color .2s; }
    a:hover { color: var(--primary-light); }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(255,255,255,.95); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 1140px; margin: 0 auto; }
    .nav-brand { font-weight: 800; font-size: 1.25rem; color: var(--text); letter-spacing: -0.5px; }
    .nav-links { display: flex; gap: 32px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .875rem; font-weight: 500; transition: color .2s; }
    .nav-links a:hover { color: var(--primary); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 24px; height: 2px; background: var(--text); margin: 5px 0; transition: .3s; }

    /* HERO - two-tone horizontal, no image */
    .hero { padding: 0; min-height: 100vh; display: flex; }
    .hero-split { display: flex; width: 100%; min-height: 100vh; }
    .hero-left { flex: 1; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); display: flex; flex-direction: column; justify-content: center; padding: 140px 48px 80px; }
    .hero-right { flex: 1; background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%); display: flex; align-items: center; justify-content: center; padding: 80px 48px; }
    .hero-right-accent { font-size: clamp(5rem, 12vw, 10rem); font-weight: 800; color: rgba(255,255,255,.1); line-height: 1; }
    .hero-content h1 { font-size: clamp(2rem, 4.5vw, 3.25rem); font-weight: 800; line-height: 1.15; letter-spacing: -1px; margin-bottom: 20px; color: var(--text); }
    .hero-content h1 .highlight { color: var(--primary); }
    .hero-sub { font-size: 1.1rem; color: var(--text-muted); margin-bottom: 32px; max-width: 480px; }
    .hero-cta { display: inline-block; padding: 14px 36px; background: linear-gradient(135deg, var(--primary), var(--primary-light)); color: #fff; font-weight: 600; font-size: .95rem; border-radius: 8px; transition: transform .2s, box-shadow .2s; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(30,64,175,.25); color: #fff; }
    .stats-bar { display: flex; gap: 40px; margin-top: 48px; padding-top: 32px; border-top: 1px solid var(--border); }
    .stat-item { text-align: center; }
    .stat-value { display: block; font-size: 1.5rem; font-weight: 800; color: var(--primary); }
    .stat-label { font-size: .8rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 1px; font-weight: 600; }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-gray { background: var(--bg-gray); }
    .section-header { text-align: center; margin-bottom: 56px; }
    .section-label { display: inline-block; font-size: .75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; color: var(--primary); margin-bottom: 8px; }
    .section-title { font-size: clamp(1.5rem, 3vw, 2.25rem); font-weight: 800; letter-spacing: -0.5px; }

    /* ABOUT - two-col text + image */
    .about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
    .about-text { font-size: 1.05rem; color: var(--text-muted); line-height: 1.8; margin-bottom: 24px; }
    .btn-outline { display: inline-block; padding: 10px 28px; border: 2px solid var(--primary); color: var(--primary); border-radius: 8px; font-weight: 600; font-size: .9rem; transition: all .2s; }
    .btn-outline:hover { background: var(--primary); color: #fff; }
    .about-image-col { border-radius: 12px; overflow: hidden; background: linear-gradient(135deg, #eff6ff, #dbeafe); min-height: 320px; display: flex; align-items: center; justify-content: center; }
    .about-image-col img { width: 100%; height: 320px; object-fit: cover; display: block; border-radius: 12px; }
    .about-image-placeholder { font-size: 4rem; color: var(--primary); opacity: .2; }

    /* SERVICES */
    .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .service-card { background: var(--bg); border-radius: 12px; padding: 32px 24px; position: relative; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,.06), 0 8px 24px rgba(0,0,0,.04); transition: transform .2s, box-shadow .2s; }
    .service-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,.1); }
    .service-card-top { position: absolute; bottom: 0; left: 0; right: 0; height: 4px; background: linear-gradient(90deg, var(--primary), var(--primary-light)); }
    .service-icon { color: var(--primary); margin-bottom: 16px; }
    .service-icon .icon-char { font-size: 28px; line-height: 1; }
    .service-card h3 { font-size: 1.05rem; font-weight: 700; margin-bottom: 8px; }
    .service-card p { color: var(--text-muted); font-size: .9rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 12px; color: var(--primary); font-weight: 700; font-size: 1rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; display: block; border-radius: 10px; transition: transform .3s; }
    .gallery-item:hover img { transform: scale(1.03); }

    /* TESTIMONIALS - 2-col grid */
    .testimonials-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
    .testimonial-card { background: var(--bg); border-radius: 12px; padding: 32px; box-shadow: 0 1px 3px rgba(0,0,0,.06); border: 1px solid var(--border); }
    .testimonial-quote { font-size: 3rem; line-height: 1; color: var(--primary-light); opacity: .3; font-family: Georgia, serif; }
    .testimonial-text { font-size: .95rem; color: var(--text-muted); line-height: 1.7; margin-bottom: 20px; }
    .testimonial-stars { margin-bottom: 16px; }
    .star { color: var(--border); font-size: 1rem; }
    .star.filled { color: #f59e0b; }
    .testimonial-author { display: flex; align-items: center; gap: 12px; }
    .testimonial-avatar { width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, var(--primary), var(--primary-light)); color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: .9rem; }
    .testimonial-author strong { display: block; font-size: .9rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .8rem; }

    /* FAQ */
    .faq-list { max-width: 720px; margin: 0 auto; }
    .faq-item { background: var(--bg-gray); border-radius: 10px; padding: 24px; margin-bottom: 12px; border: 1px solid var(--border); }
    .faq-q { font-size: 1rem; font-weight: 700; margin-bottom: 8px; display: flex; align-items: center; gap: 10px; }
    .faq-q .icon-char { font-size: 18px; line-height: 1; color: var(--primary); flex-shrink: 0; }
    .faq-a { color: var(--text-muted); font-size: .9rem; line-height: 1.7; padding-left: 28px; }

    /* PRICE LIST */
    .price-list { max-width: 720px; margin: 0 auto; }
    .price-row { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; background: var(--bg); border-radius: 10px; margin-bottom: 12px; border: 1px solid var(--border); transition: border-color .2s; }
    .price-row:hover { border-color: var(--primary-light); }
    .price-info h3 { font-size: .95rem; font-weight: 700; margin-bottom: 2px; }
    .price-info p { color: var(--text-muted); font-size: .85rem; }
    .price-amount { font-size: 1.25rem; font-weight: 800; color: var(--primary); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 420px; margin: 0 auto; }
    .hours-list li { padding: 14px 0; border-bottom: 1px solid var(--border); color: var(--text-muted); font-size: .9rem; }
    .hours-list li:last-child { border-bottom: none; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: linear-gradient(135deg, var(--primary), var(--primary-light)); padding: 14px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.1rem; }
    .review-badge .star { opacity: .5; }
    .review-badge .star.filled { opacity: 1; }
    .review-text { color: #fff; font-weight: 600; font-size: .9rem; }

    /* PROMO */
    .promo-banner { background: #eff6ff; border-bottom: 1px solid var(--border); padding: 16px 0; }
    .promo-inner { display: flex; align-items: center; gap: 16px; justify-content: center; flex-wrap: wrap; }
    .promo-badge { background: var(--primary); color: #fff; padding: 6px 16px; border-radius: 20px; font-size: .85rem; font-weight: 700; }
    .promo-content strong { font-size: .9rem; }
    .promo-content span { color: var(--text-muted); font-size: .85rem; margin-left: 8px; }

    /* CONTACT - three-col cards */
    .contact-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; max-width: 1000px; margin: 0 auto; }
    .contact-card { background: var(--bg); border: 1px solid var(--border); border-radius: 12px; padding: 32px 24px; }
    .contact-card-title { font-size: .75rem; text-transform: uppercase; letter-spacing: 1px; color: var(--text-muted); font-weight: 600; margin-bottom: 12px; }
    .contact-form { display: flex; flex-direction: column; gap: 14px; }
    .contact-form input, .contact-form textarea { background: var(--bg-gray); border: 1px solid var(--border); color: var(--text); padding: 14px 16px; border-radius: 8px; font-family: var(--font); font-size: .9rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(30,64,175,.1); }
    .contact-form button { padding: 14px; background: linear-gradient(135deg, var(--primary), var(--primary-light)); color: #fff; border-radius: 8px; font-family: var(--font); font-weight: 600; font-size: .95rem; border: none; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(30,64,175,.2); }
    .contact-item { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 16px; }
    .contact-item .icon-char { color: var(--primary); flex-shrink: 0; font-size: 20px; line-height: 1; margin-top: 2px; }
    .contact-label { display: block; font-size: .75rem; text-transform: uppercase; letter-spacing: 1px; color: var(--text-muted); font-weight: 600; margin-bottom: 2px; }
    .contact-item a { color: var(--text); font-weight: 500; }
    .map-link { display: inline-block; margin-top: 4px; font-weight: 600; font-size: .85rem; color: var(--primary); }

    /* FOOTER */
    .footer { background: var(--text); padding: 48px 0; text-align: center; }
    .footer-brand { font-weight: 800; font-size: 1.1rem; color: #fff; margin-bottom: 8px; }
    .footer p { color: #94a3b8; font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(255,255,255,.98); padding: 16px 24px; gap: 16px; box-shadow: 0 4px 12px rgba(0,0,0,.1); }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .hero-split { flex-direction: column; min-height: auto; }
      .hero-left { padding: 120px 24px 60px; }
      .hero-right { min-height: 200px; }
      .stats-bar { gap: 24px; flex-wrap: wrap; }
      .about-grid { grid-template-columns: 1fr; gap: 32px; }
      .services-grid { grid-template-columns: 1fr 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-cards { grid-template-columns: 1fr; }
      .testimonials-grid { grid-template-columns: 1fr; }
    }
    @media (max-width: 480px) {
      .services-grid { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: 1fr; }
      .stats-bar { justify-content: center; }
      .hero-left { padding: 100px 16px 40px; }
      .price-row { flex-direction: column; align-items: flex-start; gap: 8px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-split">
      <div class="hero-left">
        <div class="hero-content">
          <h1><span class="highlight">${safeHeadline}</span></h1>
          <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
          ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
          <div class="stats-bar">
            ${statsItems.map(s => {
              const parts = s.split(' ');
              const val = parts[0];
              const label = parts.slice(1).join(' ');
              return `<div class="stat-item"><span class="stat-value">${val}</span><span class="stat-label">${label}</span></div>`;
            }).join('')}
          </div>
        </div>
      </div>
      <div class="hero-right">
        <span class="hero-right-accent">${safeName.charAt(0)}</span>
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <div class="footer-brand">${safeName}</div>
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
